import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        console.log('Initializing auth...');
        console.log('Stored token:', storedToken ? 'exists' : 'none');
        console.log('Stored user:', storedUser ? 'exists' : 'none');

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);

          console.log('Auth restored from localStorage');

          // Optional: Verify token by calling /auth/me
          try {
            const response = await authService.getCurrentUser();
            if (response.success && response.user) {
              setUser(response.user);
              console.log('Token verified successfully');
            }
          } catch (error) {
            console.error('Token verification failed:', error);
            // If token is invalid, clear auth
            if (error.response?.status === 401) {
              console.log('Token expired or invalid, clearing auth');
              logout();
            }
          }
        } else {
          console.log('No stored credentials found');
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // ============================================
  // FIXED: Login function with proper error handling
  // ============================================
  const login = async (email, password) => {
    console.log('Login attempt for:', email);

    try {
      // Call authService.login which returns the API response
      const response = await authService.login(email, password);

      console.log('Login response:', response);

      // Check if login was successful
      if (response.success && response.token && response.user) {
        const newToken = response.token;
        const newUser = response.user;

        console.log('Login successful:', {
          username: newUser.username,
          role: newUser.role,
          account_status: newUser.account_status
        });

        // ============================================
        // NEW: Check account_status before storing
        // ============================================
        if (newUser.account_status !== 'approved') {
          console.warn('User account not approved:', newUser.account_status);
          // Don't store token, throw error to be caught by Login component
          throw {
            response: {
              data: {
                status: newUser.account_status,
                message: 'Account not approved'
              }
            }
          };
        }

        // Store in state
        setToken(newToken);
        setUser(newUser);
        setIsAuthenticated(true);

        // Store in localStorage
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(newUser));

        console.log('✅ Login complete - Token and user stored');

        return response; // Return response for redirect logic
      } else {
        console.error('Login response missing token or user');
        throw new Error('Invalid login response');
      }
    } catch (error) {
      console.error('Login error in AuthContext:', error);
      // Re-throw error so Login component can handle it
      throw error;
    }
  };

  const logout = () => {
    console.log('Logout called');

    try {
      // Clear state
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);

      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      console.log('Logout complete');
    } catch (err) {
      console.error('Logout error:', err);
      // Clear storage anyway
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setToken(null);
      throw new Error('Logout failed');
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};